import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/a4pyev28_grok_video_2026-04-24-16-43-23.mp4";

const BharatFirst = () => {
  const videoRef = useRef(null);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let timer;
    const onPlaying = () => {
      timer = setTimeout(() => setTextVisible(true), 400);
    };
    v.addEventListener("playing", onPlaying, { once: true });
    const fallback = setTimeout(() => setTextVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => setTextVisible(true));
    }
    return () => {
      v.removeEventListener("playing", onPlaying);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="bharat-first"
      className="relative w-full overflow-hidden snap-section snap-reveal"
      data-testid="section-bharat-first"
    >
      <div className="relative w-full h-[70vh] min-h-[520px] md:h-[85vh] md:min-h-[640px]">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="bharat-video"
        />

        {/* Dark readability overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.78) 0%, rgba(5,5,10,0.4) 20%, rgba(5,5,10,0.38) 60%, rgba(5,5,10,0.82) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 28%, rgba(5,5,10,0.58) 100%)",
          }}
        />

        {/* Edge fades */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(180deg, #05050A 0%, transparent 100%)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(0deg, #05050A 0%, transparent 100%)" }}
        />

        {/* Scanlines */}
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Centered text */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          {/* Line 1: BHARAT FIRST. */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
            transition={{ duration: 1.1, ease }}
            className="font-display font-black uppercase tracking-tight leading-[1.0] text-white"
            style={{
              fontSize: "clamp(2.25rem, 8vw, 6.5rem)",
              textShadow: "0 2px 22px rgba(0,0,0,0.75)",
            }}
            data-testid="bharat-line-1"
          >
            <span
              className="inline-block text-cyan-300 interactive-text interactive-text--yellow"
              style={{
                textShadow:
                  "0 0 24px rgba(0,240,255,0.65), 0 0 52px rgba(255,222,0,0.35)",
              }}
            >
              Bharat
            </span>{" "}
            First.
          </motion.div>

          {/* Accent bar */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={textVisible ? { opacity: 1, width: "120px" } : { opacity: 0, width: 0 }}
            transition={{ duration: 1.1, ease, delay: 0.45 }}
            className="h-[1.5px] my-8 md:my-10"
            style={{
              background:
                "linear-gradient(90deg, transparent, #00F0FF 50%, transparent)",
              boxShadow: "0 0 10px rgba(0,240,255,0.55)",
            }}
          />

          {/* Line 2a: NO FILTERS. */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 0.65 }}
            className="font-display font-black uppercase tracking-tight leading-[1.04] text-white"
            style={{
              fontSize: "clamp(1.75rem, 5.2vw, 4.25rem)",
              textShadow: "0 2px 20px rgba(0,0,0,0.7)",
            }}
            data-testid="bharat-line-2a"
          >
            No Filters.
          </motion.div>

          {/* Line 2b: NO COMPROMISE. (stronger glow) */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 1.05, ease, delay: 0.95 }}
            className="mt-3 md:mt-4 font-display font-black uppercase tracking-tight leading-[1.04] text-white interactive-text interactive-text--magenta"
            style={{
              fontSize: "clamp(1.85rem, 5.6vw, 4.5rem)",
              textShadow:
                "0 0 22px rgba(0,240,255,0.5), 0 0 48px rgba(255,0,255,0.35), 0 2px 22px rgba(0,0,0,0.7)",
            }}
            data-testid="bharat-line-2b"
          >
            No Compromise.
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BharatFirst;
